import { useCallback, useEffect, useRef } from 'react';
import type { TableSelectedCell } from '@/components/custom/erp/input-table-v2';
import { useTableRows } from '@/components/custom/erp/input-table-v2';
import { roundNumber } from '@/lib/formatUtils';

export type SelectedCellInfo = TableSelectedCell;

export interface DetailRow {
  uuid?: string | null;
  line?: number;
  gia_nt?: number;
  tien_nt?: number;
  ma_vt_data?: MuaHangRecord;
  dvt?: MuaHangRecord;
  thue_nt?: number;
  so_luong?: number;
  tl_ck?: number;
  ck_nt?: number;
  ck?: number;
  tien_tck_nt?: number;
  tien_tck?: number;
  ma_thue_data?: {
    thue_suat?: number;
    tax_rate?: number;
    [key: string]: MuaHangRecord;
  };
  [key: string]: MuaHangRecord;
}

type PriceLookupMeta = {
  isInitializing: boolean;
};

type FetchKhoFromBoPhanFn = (slug: string, maBp: string) => Promise<MuaHangRecord>;
type PriceLookupHandler = (
  row: DetailRow,
  field: string,
  value: MuaHangRecord,
  updateRow: (rowUuid: string, fields: Partial<DetailRow>) => void,
  meta: PriceLookupMeta
) => void;

export function useDetailRows(
  initialRows: DetailRow[] = [],
  onChange?: (rows: DetailRow[]) => void,
  onFieldValueChange?: (row: DetailRow, field: string, value: MuaHangRecord) => Partial<DetailRow>,
  priceLookupHandler?: PriceLookupHandler
) {
  const priceLookupHandlerRef = useRef<PriceLookupHandler | undefined>(priceLookupHandler);

  const isInitializingRef = useRef(true);

  useEffect(() => {
    priceLookupHandlerRef.current = priceLookupHandler;
  }, [priceLookupHandler]);

  useEffect(() => {
    isInitializingRef.current = false;
  }, []);

  const handleFieldValueChange = (row: DetailRow, field: string, value: MuaHangRecord): Partial<DetailRow> => {
    const extraFields: Partial<DetailRow> = {};

    if (field === 'gia_nt' || field === 'so_luong') {
      const so_luong = Number(field === 'so_luong' ? value : row.so_luong) || 1;
      const gia_nt = Number(field === 'gia_nt' ? value : row.gia_nt) || 0;
      const tien_nt = roundNumber(gia_nt * so_luong);
      const taxRate = row.ma_thue_data?.thue_suat || row.ma_thue_data?.tax_rate || 0;

      extraFields.tien_nt = tien_nt;
      extraFields.thue_nt = (tien_nt * taxRate) / 100;
    } else if (field === 'tien_nt') {
      const tien = Number(value) || 0;
      const so_luong = Number(row.so_luong) || 0;
      const taxRate = row.ma_thue_data?.thue_suat || row.ma_thue_data?.tax_rate || 0;
      extraFields.tien_nt = tien;
      extraFields.thue_nt = (tien * taxRate) / 100;
      if (so_luong > 0) {
        extraFields.gia_nt = Math.round((tien / so_luong) * 100) / 100;
      }
    }

    if (field === 'ma_vt_data') {
      extraFields.dvt = value?.dvt_nl_data?.uuid || value?.dvt_data?.uuid || row.dvt;
    }

    const shouldCalculateTax = ['ma_thue_data'].includes(field);
    if (shouldCalculateTax) {
      const baseAmount = Number(row.tien_nt) || 0;
      const taxRate = value?.thue_suat || value?.tax_rate || 0;
      extraFields.thue_nt = (baseAmount * taxRate) / 100;
    }

    const customFields = onFieldValueChange?.(row, field, value);
    return { ...extraFields, ...customFields };
  };

  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange: originalHandleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<MuaHangRecord>({
    initialRows,
    defaultRows: 1,
    onChange,
    onFieldValueChange: handleFieldValueChange,
    defaultField: 'dien_giai'
  });
  const updateRowByUuid = useCallback(
    (uuid: string, fields: Partial<DetailRow>) => {
      setRows(prevRows => prevRows.map(r => (r.uuid === uuid ? { ...r, ...fields } : r)));
    },
    [setRows]
  );

  const setPriceLookupHandler = useCallback((handler?: PriceLookupHandler) => {
    priceLookupHandlerRef.current = handler;
  }, []);

  const handleCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      originalHandleCellValueChange(rowUuid, field, newValue);

      const handler = priceLookupHandlerRef.current;
      if (!handler) return;

      setRows(prevRows => {
        const row = prevRows.find(r => r.uuid === rowUuid);
        if (!row) return prevRows;

        const updatedRow = { ...row, [field]: newValue };

        handler(updatedRow, field, newValue, updateRowByUuid, {
          isInitializing: isInitializingRef.current
        });

        return prevRows;
      });
    },
    [originalHandleCellValueChange, setRows, updateRowByUuid]
  );

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting,
    setPriceLookupHandler
  };
}
